import React, { useMemo, useState } from 'react';
import { Box, Card, Typography, Button, ToggleButton, ToggleButtonGroup } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { RefreshRounded as RefreshIcon, SensorsOffRounded as QuietIcon } from '@mui/icons-material';
import { useLiveScores } from '../hooks';
import { tokens } from '../theme';
import { PageHeader, TeamCrest, EmptyState, CardGridSkeleton, LiveDot, segmentedSx } from './ui';

function ScoreRow({ match: m }) {
  const homeWon = m.is_finished && m.home_score > m.away_score;
  const awayWon = m.is_finished && m.away_score > m.home_score;
  const team = (name, logo, score, won) => (
    <Box display="flex" alignItems="center" gap={1.25} minWidth={0}>
      <TeamCrest src={logo} name={name} size={24} />
      <Typography variant="body2" noWrap flex={1} title={name} sx={{ fontWeight: won ? 700 : 500, color: m.is_finished && !won ? 'text.secondary' : 'text.primary' }}>
        {name}
      </Typography>
      <Typography sx={{ fontFamily: '"Space Grotesk", sans-serif', fontWeight: 700, minWidth: 18, textAlign: 'right', color: m.is_finished && !won ? 'text.secondary' : 'text.primary' }}>
        {score}
      </Typography>
    </Box>
  );

  return (
    <Box display="grid" gridTemplateColumns="56px minmax(0,1fr)" alignItems="center" gap={1.5}
      sx={{ px: 2, py: 1.5, '&:not(:last-of-type)': { borderBottom: `1px solid ${tokens.border}` } }}>
      <Box textAlign="center">
        {m.is_live ? (
          <Box display="flex" flexDirection="column" alignItems="center" gap={0.5}>
            <LiveDot />
            <Typography variant="caption" fontWeight={700} sx={{ color: tokens.status.live }}>
              {m.status_code === 'STATUS_HALFTIME' ? 'HT' : m.minute || 'LIVE'}
            </Typography>
          </Box>
        ) : (
          <Typography variant="caption" fontWeight={700} color="text.secondary">{m.is_finished ? 'FT' : m.status}</Typography>
        )}
      </Box>
      <Box display="flex" flexDirection="column" gap={0.75} minWidth={0}>
        {team(m.home_team, m.home_logo, m.home_score, homeWon)}
        {team(m.away_team, m.away_logo, m.away_score, awayWon)}
      </Box>
    </Box>
  );
}

const LiveScores = () => {
  const { matches, updatedAt, loading, reload } = useLiveScores();
  const [filter, setFilter] = useState('all');

  const groups = useMemo(() => {
    const filtered = matches.filter(m => filter === 'all' || (filter === 'live' ? m.is_live : m.is_finished));
    const byLeague = {};
    for (const m of filtered) (byLeague[m.league] ||= []).push(m);
    // Leagues with live games first, live games first within a league
    return Object.entries(byLeague)
      .map(([league, list]) => [league, list.sort((a, b) => (b.is_live - a.is_live) || new Date(a.kickoff_time) - new Date(b.kickoff_time))])
      .sort(([, a], [, b]) => b.filter(m => m.is_live).length - a.filter(m => m.is_live).length);
  }, [matches, filter]);

  const liveCount = matches.filter(m => m.is_live).length;

  return (
    <Box>
      <PageHeader
        title="Live scores"
        subtitle={updatedAt ? `Updates every 30 seconds · last update ${new Date(updatedAt).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit', second: '2-digit' })}` : 'Updates every 30 seconds'}
        actions={<Button variant="outlined" startIcon={<RefreshIcon />} onClick={reload}>Refresh</Button>}
      />

      <ToggleButtonGroup
        size="small" exclusive value={filter} onChange={(_, v) => v && setFilter(v)}
        sx={{ ...segmentedSx, mb: 2.5 }}
      >
        <ToggleButton value="all">All ({matches.length})</ToggleButton>
        <ToggleButton value="live">Live ({liveCount})</ToggleButton>
        <ToggleButton value="finished">Finished ({matches.length - liveCount})</ToggleButton>
      </ToggleButtonGroup>

      {loading ? (
        <CardGridSkeleton count={2} height={160} />
      ) : groups.length === 0 ? (
        <EmptyState icon={<QuietIcon />} title={filter === 'live' ? 'Nothing live right now' : 'No matches yet today'} body="Matches appear here once they kick off." />
      ) : (
        <Box display="grid" gap={2} gridTemplateColumns={{ xs: '1fr', lg: 'repeat(2, minmax(0,1fr))' }} alignItems="start">
          {groups.map(([league, list]) => (
            <Card key={league}>
              <Box display="flex" justifyContent="space-between" alignItems="center" px={2} py={1.25}
                sx={{ bgcolor: alpha('#fff', 0.02), borderBottom: `1px solid ${tokens.border}` }}>
                <Typography variant="subtitle2">{league}</Typography>
                {list.some(m => m.is_live) && (
                  <Typography variant="caption" fontWeight={700} sx={{ color: tokens.status.live }}>
                    {list.filter(m => m.is_live).length} live
                  </Typography>
                )}
              </Box>
              {list.map(m => <ScoreRow key={m.id} match={m} />)}
            </Card>
          ))}
        </Box>
      )}
    </Box>
  );
};

export default LiveScores;
